import { isEmpty } from '@sgme/fp';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { PriceAdjustmentUserOverride, TradeRequestPayload } from 'epic/common-form/Trade';
import type { QuoteKeys, QuoteKeysPrefix } from 'locales/quoteKeys';
import { NotionalType } from 'models/common/notional';
import type { Custodian } from 'models/custodian';
import type { FormError, FormRecord } from 'models/formError';
import type { LoadingResult, MergeDeal } from 'models/forms/AddonFormResult';
import type { DealSplitData } from 'models/forms/DealSplitData';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { AddonQuoteRequestPayload } from 'models/quote/AddonQuoteRequestPayload';
import * as fromPending from 'state/quotes/pendings/pendings.selectors';
import * as fromQuoteResult from 'state/quotes/results/results.selectors';
import type { PriceEnhancement, PriceEnhancementMode, TradeStatus } from 'state/quotes/trades/trades.model';
import { getIsTradeEnabled } from 'state/selectors';
import {
  getQuoteRejectionsByQuoteId,
  getTradeInfosByQuoteId,
  getUserPriceEnhancementByQuoteId,
  getUserSponsoFeesByQuoteId,
} from 'state/selectors/common/commonForm';
import { getTradeConfirmationByQuoteId } from 'state/selectors/common/temp';
import type { AppState } from 'state/state.model';
import type { WorkFlowStatus } from 'state/static/staticForm.selectors';
import { toExecutionBackToBack } from 'state/uiPanelState/tradeConfirmation/backToBack.utils';
import type { BackToBackType } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';
import * as fromTradeConfirmation from 'state/uiPanelState/tradeConfirmation/tradeConfirm.selectors';
import {
  getUserPreferencesLanguage,
  getUserPreferencesUnderlyingCodeType,
} from 'state/userPreferences/userPreferences.selectors';

import type { AddonFormLoadingStatus, RemunerationMode, SolveMode, TradeClientSide } from './addonForm.common';
import { defaultAddonSolvingModes } from './addonForm.common';

const getTab = (state: AppState, tabId: string) => state.addonForm[tabId];
const getForm = (state: AppState, tabId: string) => getTab(state, tabId)?.form;
export const getInfo = (state: AppState, tabId: string) => getTab(state, tabId)?.info;

export function getQuoteRequest(state: AppState, tabId: string, requestedQuoteId: string): AddonQuoteRequestPayload {
  return {
    quoteId: requestedQuoteId,
    originalQuoteId: getOriginalQuoteId(state, tabId),
    productId: getProductId(state, tabId)!,
    tradeSide: getTradeSide(state, tabId)!,
    notional: getAddonNotional(state, tabId),
    notionalInUnits: getAddonNotionalInUnits(state, tabId),
    notionalType: getNotionalType(state, tabId)!,
    bdrId: getSelectedOnBehalfClient(state, tabId)!.id,
    requestPrice: getRequestPrice(state, tabId),
    fixedAmountInPercent: getFixedAmountInPercent(state, tabId),
    dealSplits: getDealSplit(state, tabId)!,
    remunerationMode: getRemunerationMode(state, tabId)!,
    solveFor: getSolveFor(state, tabId)!,
    xMargin: getXMargin(state, tabId),
    mergeTradeRequested: getMergeDealsRequested(state, tabId)!,
    mergeTradeId: getSelectedTradeReference(state, tabId),
    wrapper: getWrapper(state, tabId)!,
    primaryDealId: undefined,
    instrumentContractName: getInstrumentContractName(state, tabId),
    fundingLockDate: getFundingLockDate(state, tabId),
    salesBeneficiary: getSalesBeneficiary(state, tabId),
  };
}

export function getProductId(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.productId;
}
export function getInstrumentContractName(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.instrumentContractName;
}

export function getInstrumentName(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.instrumentName;
}
export function getWrapper(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.wrapper;
}

export function getIsinCode(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.isinCode;
}

export function getFileCode(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.fileCode;
}

export function isOtc(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.isOtc;
}
export function isShareAccDec(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.isShareAccDec;
}
export function getAssetTypes(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.assetTypes;
}
export function getCurrency(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.currency;
}

function getBackToBackContextType(state: AppState, tabId: string): BackToBackType | undefined {
  const backToBackType = getInfo(state, tabId)?.backToBackType;
  if (backToBackType === 'HedgingTrade' || backToBackType === 'ClientTrade' || backToBackType === 'None') {
    return backToBackType;
  }
  return undefined;
}

export function isBackToBack(state: AppState, tabId: string) {
  return getForm(state, tabId)?.backToBackIsBackToBack ?? getInfo(state, tabId)?.isBackToBack;
}
export function isDelayedBackToBack(state: AppState, tabId: string) {
  return getForm(state, tabId)?.backToBackIsDelayedBackToBack ?? getInfo(state, tabId)?.isDelayedBackToBack;
}
export function getBackToBackType(state: AppState, tabId: string) {
  const contextType = getBackToBackContextType(state, tabId);
  return getForm(state, tabId)?.backToBackType ?? contextType;
}
export function getBackToBackFileCode(state: AppState, tabId: string) {
  return getForm(state, tabId)?.backToBackFileCode;
}

export function getBackToBackNoteNominal(state: AppState, tabId: string) {
  return getForm(state, tabId)?.backToBackNoteNominal;
}

export function getBackToBackIsDeltaAdjusted(state: AppState, tabId: string) {
  return getForm(state, tabId)?.backToBackIsDeltaAdjusted;
}
export function getFundingLockDate(state: AppState, tabId: string) {
  return getForm(state, tabId)?.fundingLockDate;
}

export function getOriginalQuoteId(state: AppState, tabId: string) {
  return getInfo(state, tabId)?.originalQuoteId;
}
export function isLoading(state: AppState, tabId: string) {
  const status = getTab(state, tabId)?.loadingStatus;
  return status === undefined || status === 'Pending' || status === 'Resetting';
}

export function getLoadingStatus(state: AppState, tabId: string) {
  return getTab(state, tabId)?.loadingStatus;
}
export function getLoadingError(state: AppState, tabId: string) {
  return getTab(state, tabId)?.loadingError;
}

const sides: TradeClientSide[] = ['buy', 'sell'];

export function getTradeClientSides(): TradeClientSide[] {
  return sides;
}

export function getTradeClientSide(state: AppState, tabId: string) {
  return getForm(state, tabId)?.tradeClientSide;
}

export function getAddonAction(state: AppState, tabId: string) {
  return getForm(state, tabId)?.addonAction;
}

export function getTradeSide(state: AppState, tabId: string) {
  const tradeClientSide = getTradeClientSide(state, tabId);
  switch (tradeClientSide) {
    case 'buy':
      return 'Sell';
    case 'sell':
      return 'Buy';
    default:
      return undefined;
  }
}

export function getSelectedOnBehalfClient(state: AppState, tabId: string) {
  return getForm(state, tabId)?.selectedOnBehalfClient;
}

export function getCustodians(state: AppState, tabId: string) {
  return getForm(state, tabId)?.custodians;
}

export function getMergeDealsRequested(state: AppState, tabId: string) {
  const shouldMergeDeals = getForm(state, tabId)?.mergeDealsRequested;
  return shouldMergeDeals;
}

export function getMergeableDeals(state: AppState, tabId: string) {
  return getForm(state, tabId)?.mergeableDeals;
}

export function getSelectedTradeReference(state: AppState, tabId: string) {
  return getForm(state, tabId)?.selectedTradeReference;
}
export function getNotionalType(state: AppState, tabId: string) {
  return getForm(state, tabId)?.notionalType;
}

export function getAddonNotional(state: AppState, tabId: string) {
  return getForm(state, tabId)?.addonNotional;
}

export function getAddonNotionalInUnits(state: AppState, tabId: string) {
  return getForm(state, tabId)?.addonNotionalInUnits;
}

export function getDailyNumberOfShares(state: AppState, tabId: string) {
  return getForm(state, tabId)?.dailyNumberOfShares;
}

export function getTotalNumberOfAccumulationDays(state: AppState, tabId: string) {
  return getForm(state, tabId)?.totalNumberOfAccumulationDays;
}

export function getDealSplit(state: AppState, tabId: string) {
  return getForm(state, tabId)?.dealSplit;
}

export function getRemunerationMode(state: AppState, tabId: string) {
  return getForm(state, tabId)?.remunerationMode;
}

export function getSalesBeneficiaryRtfeLogin(state: AppState, tabId: string) {
  return getForm(state, tabId)?.salesBeneficiary;
}

export function getSalesBeneficiary(state: AppState, tabId: string) {
  const salesBeneficiaries = getAvailableSalesBeneficiaries(state, tabId);
  const salesBeneficiary = getSalesBeneficiaryRtfeLogin(state, tabId);
  return salesBeneficiaries?.find(obj => obj.value.rtfeLogin === salesBeneficiary)?.value;
}

export function getSalesDesk(state: AppState, tabId: string) {
  return getForm(state, tabId)?.salesDesk;
}

export function getAvailableSalesBeneficiaries(state: AppState, tabId: string) {
  return getForm(state, tabId)?.availableSalesBeneficiaries;
}

export function getAvailableSalesDesks(state: AppState, tabId: string) {
  return getForm(state, tabId)?.availableSalesDesks;
}

export function getRequestPrice(state: AppState, tabId: string) {
  const solveFor = getSolveFor(state, tabId);
  const remunerationMode = getRemunerationMode(state, tabId)!;

  if (solveFor === 'OfferPrice' || solveFor === 'UpfrontFee' || solveFor === 'FixedAmount') {
    return undefined;
  }

  switch (remunerationMode) {
    case 'Reoffer':
      return getOfferPrice(state, tabId);
    case 'Upfront':
      return getUpFrontFee(state, tabId);
    case 'Premium': {
      return undefined;
    }
    default:
      return undefined;
  }
}

export function getFixedAmountInPercent(state: AppState, tabId: string) {
  const solveFor = getSolveFor(state, tabId);
  const remunerationMode = getRemunerationMode(state, tabId)!;

  if (solveFor === 'OfferPrice' || solveFor === 'UpfrontFee' || solveFor === 'FixedAmount') {
    return undefined;
  }

  return remunerationMode === 'Premium' ? getFixedAmount(state, tabId) : undefined;
}

export function getSolveModes(state: AppState, tabId: string) {
  return getForm(state, tabId)?.solveModes;
}
export function isReadOnly(state: AppState, tabId: string) {
  return getForm(state, tabId)?.isReadOnly;
}

export function getSolveFor(state: AppState, tabId: string) {
  return getForm(state, tabId)?.solveFor;
}
export function getAvailableSolvingModes(state: AppState, tabId: string) {
  return getForm(state, tabId)?.availableSolvingModes || defaultAddonSolvingModes;
}
export function getXMargin(state: AppState, tabId: string) {
  return getForm(state, tabId)?.xMargin;
}
export function getUpFrontFee(state: AppState, tabId: string) {
  return getForm(state, tabId)?.upFrontFee;
}
export function getOfferPrice(state: AppState, tabId: string) {
  return getForm(state, tabId)?.offerPrice;
}

export function getFixedAmount(state: AppState, tabId: string) {
  return getForm(state, tabId)?.fixedAmount;
}

export function getLastQuoteId(state: AppState, tabId: string) {
  return getForm(state, tabId)?.lastQuoteId;
}

export function getLastQuoteExternalId(state: AppState, tabId: string) {
  return getForm(state, tabId)?.lastQuoteExternalId;
}

function mapPriceAdjustmentUserOverride(
  mode: PriceEnhancementMode,
  enhancement: PriceEnhancement | undefined,
): PriceAdjustmentUserOverride | null {
  if (mode === 'None' || enhancement === undefined) {
    return null;
  }
  const { tradingOffer, xMargin: salesMargin } = enhancement;
  return { tradingOffer, salesMargin };
}

export function getTradeRequest(state: AppState, tabId: string) {
  const quoteId = getLastQuoteId(state, tabId)!;
  const quote = getQuoteResult(state, quoteId)!;

  const externalQuoteId = quote!.quoteExternalId!;
  const remunerationMode = getRemunerationMode(state, tabId)!;

  const mode = getPriceEnhancementMode(state, tabId, remunerationMode);

  const priceEnhancement = getUserPriceEnhancementByQuoteId(state, quoteId);
  const sponsoFees = getUserSponsoFeesByQuoteId(state, quoteId);
  const priceAdjustmentUserOverride = mapPriceAdjustmentUserOverride(mode, priceEnhancement);
  const addonAction = getAddonAction(state, tabId);
  const defaultUnderlyingCodeType = getUserPreferencesUnderlyingCodeType(state);
  const languageCode = getUserPreferencesLanguage(state);

  const tradeInfo = getTradeInfo(state, tabId)!;
  const wrapper = getWrapper(state, tabId);
  const originalQuoteId = getOriginalQuoteId(state, tabId);

  const { priceEnhancement: defaultPriceEnhancement } = quote;

  const tradeRequest: TradeRequestPayload = {
    externalQuoteId: externalQuoteId,
    internalQuoteId: quoteId,
    originalQuoteId,
    productSubType: quote.productSubType,
    wrapper: wrapper,
    screenMode: 'NewAddon',
    side: undefined,
    issuePrice: defaultPriceEnhancement?.issuePrice!,
    priceAdjustmentUserOverride,
    sponsoFees,
    executionOnly: false,
    comment: tradeInfo.comment,
    defaultUnderlyingCodeType,
    languageCode,
    addonAction: addonAction,
    backToBack: toExecutionBackToBack(getTradeConfirmationByQuoteId(state, quoteId)),
  };

  return tradeRequest;
}

export function hasBackToBack(state: AppState, quoteId: string | undefined) {
  if (!quoteId) {
    return false;
  }
  const tradeConfirmation = fromTradeConfirmation.getTradeConfirmation(state.uiPanel.tradeConfirmations, quoteId);
  return tradeConfirmation?.isBackToBack.value ?? false;
}

export function getQuoteResult(state: AppState, quoteId: string | undefined) {
  if (!quoteId) {
    return undefined;
  }
  return fromQuoteResult.getQuoteResult(state.quotes.results, quoteId);
}

export const getQuoteDetails = (state: AppState, tabId: string) => getTab(state, tabId)?.quoteDetails;

export function hasGreeksOrFeesOrMetrics(state: AppState, tabId: string) {
  return hasGreeks(state, tabId) || hasFees(state, tabId) || hasMetrics(state, tabId);
}

export function hasGreeks(state: AppState, tabId: string) {
  const quoteDetails = getQuoteDetails(state, tabId);
  return !isEmpty(quoteDetails?.Greeks?.cells);
}

export function hasFees(state: AppState, tabId: string) {
  const quoteDetails = getQuoteDetails(state, tabId);
  return !isEmpty(quoteDetails?.Fees);
}

export function hasMetrics(state: AppState, tabId: string) {
  const quoteDetails = getQuoteDetails(state, tabId);
  return quoteDetails?.Metrics !== undefined;
}

export function hasHistoricalAverageCorrelations(state: AppState, tabId: string) {
  const quoteDetails = getQuoteDetails(state, tabId);
  return (
    quoteDetails?.historicalAverageCorrelations !== undefined &&
    quoteDetails.historicalAverageCorrelations.cells.length > 0
  );
}

export function hasPrices(state: AppState, tabId: string) {
  const quoteDetails = getQuoteDetails(state, tabId);
  return !isEmpty(quoteDetails?.prices?.cells);
}

export function hasProductGreeks(state: AppState, tabId: string) {
  const quoteDetails = getQuoteDetails(state, tabId);
  return !isEmpty(quoteDetails?.productGreeks);
}
export function getQuoteInfo(state: AppState, tabId: string) {
  return (
    fromPending.getQuoteInfos(state.quotes.pendings, tabId) ??
    fromQuoteResult.getQuoteInfos(state.quotes.results, getLastQuoteId(state, tabId))
  );
}

export function getTradeInfo(state: AppState, tabId: string) {
  const quoteId = getLastQuoteId(state, tabId);
  return quoteId ? getTradeInfosByQuoteId(state, quoteId) : undefined;
}

export function getTechnicalErrors(state: AppState, tabId: string): ApiErrorMessage | undefined {
  const quoteInfos = getQuoteInfo(state, tabId);
  const quoteId = getLastQuoteId(state, tabId);

  const tradeInfos = getTradeInfosByQuoteId(state, quoteId!);

  return tradeInfos?.technicalError ?? quoteInfos?.technicalError;
}

export function getPriceEnhancementMode(
  state: AppState,
  tabId: string,
  remunerationMode?: RemunerationMode,
): PriceEnhancementMode {
  if (remunerationMode === undefined) {
    return 'None';
  }

  const quoteDetails = getQuoteDetails(state, tabId);

  if (!quoteDetails) {
    return 'None';
  }

  switch (remunerationMode) {
    case 'Reoffer':
      return 'Reoffer';
    case 'Upfront':
      return 'UpfrontFee';
    case 'Premium':
      return 'Premium';
    default:
      return 'None';
  }
}
export function isPriceInconsistent(state: AppState, tabId: string): boolean {
  return getInfo(state, tabId)?.isPriceInconsistent ?? false;
}

export function getWorkflowStatus(state: AppState, tabId: string): WorkFlowStatus {
  const loadingStatus = getLoadingStatus(state, tabId);
  const hasTradableRights = getIsTradeEnabled(state);

  if (loadingStatus === 'Pending' || loadingStatus === 'Resetting') {
    return 'LOADING';
  }

  const quoteInfo = getQuoteInfo(state, tabId);
  if (getInfo(state, tabId)?.quoteState === 'Previous') {
    return 'QUOTED_PREVIOUSLY';
  }
  const validationErrors = getValidationErrors(state, tabId);

  if (!quoteInfo) {
    if (!validationErrors || validationErrors.length === 0) {
      return 'READY';
    } else {
      return 'INITIALIZED';
    }
  }

  if (quoteInfo.technicalError !== undefined) {
    return 'ERROR';
  }

  if (quoteInfo.quoteRequestStartingTimeInMillis) {
    return 'QUOTING';
  }

  const quoteId = getLastQuoteId(state, tabId);

  const tradeInfos = getTradeInfosByQuoteId(state, quoteId!);

  if (tradeInfos?.tradeStatus !== undefined) {
    return mapTradeStatusToWorkflowStatus(tradeInfos.tradeStatus);
  }

  const quoteResult = getQuoteResult(state, quoteId);

  if (quoteResult) {
    if (quoteResult.status === 'Quoted') {
      if (hasTradableRights) {
        return 'QUOTED_TRADABLE'; // NO TRADE AUTH FOR ADDON
      } else {
        return 'QUOTED'; // NO TRADE AUTH FOR ADDON
      }
    } else {
      return 'REJECTED';
    }
  }

  return 'INITIALIZED';
}

function mapTradeStatusToWorkflowStatus(status: TradeStatus): WorkFlowStatus {
  switch (status) {
    case 'Pending':
      return 'TRADING';
    case 'Done':
      return 'TRADED';
    case 'Rejected':
      return 'REJECTED';
    default:
      return 'ERROR';
  }
}

export type QuoteFormError = FormError<FormRecord, QuoteKeys, QuoteKeysPrefix>;

const DEAL_SPLIT_UNALLOCATED_ERROR: QuoteFormError = {
  fields: ['dealSplit'],
  severity: 'Error',
  translationKey: 'addon.dealSplitUnallocatedAmount',
  values: {},
  isDisplayedInSummary: true,
};
const validateDealSplit = (
  dealSplit: readonly DealSplitData[],
  notionalType: NotionalType,
  addonNotional?: number,
): readonly QuoteFormError[] => {
  const errors: QuoteFormError[] = [];

  if (notionalType === 'Units') {
    return errors;
  }
  if (addonNotional === undefined) {
    return errors;
  }
  if (dealSplit.length === 0) {
    return errors;
  }
  const allocatedNotional = dealSplit.map(data => data.NotionalAmountInGrid ?? 0).reduce((v1, v2) => v1 + v2);

  if (allocatedNotional === addonNotional) {
    return errors;
  }

  errors.push(DEAL_SPLIT_UNALLOCATED_ERROR);

  return errors;
};

const MISSING_SALES_COUNTERPART: QuoteFormError = {
  fields: ['salesCounterpart'],
  severity: 'Error',
  translationKey: 'undefined',
  values: { field: 'Sales counterpart' },
};
const validateSalesCounterPart = (salesCounterPart: ISalesCounterpart | null): QuoteFormError[] => {
  if (salesCounterPart === null) {
    return [MISSING_SALES_COUNTERPART];
  }
  return [];
};

const NO_DEAL_SELECTED_ERROR: QuoteFormError = {
  fields: ['mergeDeals'],
  severity: 'Error',
  translationKey: 'addon.noDealSelected',
  values: {},
  isDisplayedInSummary: true,
};
const NO_MERGEABLE_DEALS_ERROR: QuoteFormError = {
  fields: ['mergeDeals'],
  severity: 'Error',
  translationKey: 'addon.noMergeableDeals',
  values: {},
  isDisplayedInSummary: true,
};
const validateMergeDeals = (
  mergeDealsRequested: boolean,
  result: LoadingResult<ReadonlyArray<MergeDeal>> | undefined,
  selectedTradeId: string | undefined,
  isOtc: boolean,
): QuoteFormError[] => {
  if (result === undefined) {
    return [NO_DEAL_SELECTED_ERROR];
  }

  if (!result.isSuccess) {
    if (result.error === 'TIMEOUT') {
      return [
        {
          fields: ['mergeDeals'],
          severity: isOtc || mergeDealsRequested ? 'Error' : 'Warning',
          translationKey: 'addon.mergeableDeals.loadingError',
          values: { message: 'Timeout exceeded' },
          isDisplayedInSummary: true,
        },
      ];
    }

    return [
      {
        fields: ['mergeDeals'],
        severity: 'Error',
        translationKey: 'addon.mergeableDeals.loadingError',
        values: { message: result.error ?? 'No error message provided' },
        isDisplayedInSummary: true,
      },
    ];
  }

  if (isOtc && (!result.data || result.data.length === 0)) {
    return [NO_MERGEABLE_DEALS_ERROR];
  }
  if (mergeDealsRequested && !selectedTradeId) {
    return [NO_DEAL_SELECTED_ERROR];
  }
  return [];
};

const MISSING_XMARGIN_ERROR: QuoteFormError = {
  fields: ['xMargin'],
  severity: 'Error',
  translationKey: 'xMarginNotDefined',
  values: {},
};

const NEGATIVE_XMARGIN_WARNING: QuoteFormError = {
  fields: ['xMargin'],
  severity: 'Warning',
  translationKey: 'fieldIsLowerOrEqualZero',
  values: { field: 'X margin' },
};

const validateXMargin = (solveMode: SolveMode, xMargin?: number): QuoteFormError[] => {
  if (xMargin === undefined && solveMode !== 'XMargin') {
    return [MISSING_XMARGIN_ERROR];
  }
  if (xMargin !== undefined && xMargin <= 0) {
    return [NEGATIVE_XMARGIN_WARNING];
  }
  return [];
};

const MISSING_UPFRONT_FEE_ERROR: QuoteFormError = {
  fields: ['upFrontFee'],
  severity: 'Error',
  translationKey: 'UpfrontFeeNotDefined',
  values: {},
};
const validateUpFrontFee = (
  remunerationMode: RemunerationMode,
  solveMode: SolveMode,
  upFrontFee?: number,
): QuoteFormError[] => {
  if (upFrontFee === undefined && remunerationMode === 'Upfront' && solveMode !== 'UpfrontFee') {
    return [MISSING_UPFRONT_FEE_ERROR];
  }
  return [];
};

const MISSING_OFFER_PRICE_ERROR: QuoteFormError = {
  fields: ['offerPrice'],
  severity: 'Error',
  translationKey: 'OfferPriceNotDefined',
  values: {},
};
const validateOfferPrice = (
  remunerationMode: RemunerationMode,
  solveMode: SolveMode,
  offerPrice?: number,
): QuoteFormError[] => {
  if (offerPrice === undefined && remunerationMode === 'Reoffer' && solveMode !== 'OfferPrice') {
    return [MISSING_OFFER_PRICE_ERROR];
  }
  return [];
};

const MISSING_FIXED_AMOUNT_ERROR: QuoteFormError = {
  fields: ['fixedAmount'],
  severity: 'Error',
  translationKey: 'FixedAmountNotDefined',
  values: {},
};
const validateFixedAmount = (
  remunerationMode: RemunerationMode,
  solveMode: SolveMode,
  fixedAmount?: number,
): QuoteFormError[] => {
  if (fixedAmount === undefined && remunerationMode === 'Premium' && solveMode !== 'FixedAmount') {
    return [MISSING_FIXED_AMOUNT_ERROR];
  }
  return [];
};

const MISSING_NOTIONAL_ERROR: QuoteFormError = {
  fields: ['addonNotional'],
  severity: 'Error',
  translationKey: 'addon.noAddonNotional',
  values: {},
};
const validateNotional = (notionalType: NotionalType, addonNotional?: number): QuoteFormError[] => {
  if (notionalType === 'Units') {
    return [];
  }

  if (addonNotional === undefined || addonNotional === 0) {
    return [MISSING_NOTIONAL_ERROR];
  }
  return [];
};

const MISSING_DAILY_SHARES_COUNT_ERROR: QuoteFormError = {
  fields: ['dailyNumberOfShares'],
  severity: 'Error',
  translationKey: 'addon.noDailyNumberOfShares',
  values: {},
};
const MISSING_ACCUMULATION_DAYS_COUNT_ERROR: QuoteFormError = {
  fields: ['totalNumberOfAccumulationDays'],
  severity: 'Error',
  translationKey: 'addon.noTotalNumberOfAccumulationDays',
  values: {},
};
const validateNotionalInUnits = (
  notionalType: NotionalType,
  dailyNumberOfShares?: number,
  totalNumberOfAccumulationDays?: number,
): QuoteFormError[] => {
  if (notionalType === 'Currency') {
    return [];
  }

  const errors: QuoteFormError[] = [];

  if (dailyNumberOfShares === undefined || dailyNumberOfShares === 0) {
    errors.push(MISSING_DAILY_SHARES_COUNT_ERROR);
  }
  if (totalNumberOfAccumulationDays === undefined || totalNumberOfAccumulationDays === 0) {
    errors.push(MISSING_ACCUMULATION_DAYS_COUNT_ERROR);
  }
  return errors;
};

const CUSTODIAN_FETCH_FAILED_ERROR: QuoteFormError = {
  fields: ['dealSplit'],
  severity: 'Error',
  translationKey: 'addon.custodians.error',
  values: {},
  isDisplayedInSummary: true,
};
const MISSING_CUSTODIANS_ERROR: QuoteFormError = {
  fields: ['dealSplit'],
  severity: 'Error',
  translationKey: 'noCustodian',
  values: {},
  isDisplayedInSummary: true,
};
const validateCustodians = (
  { isSuccess, data: custodians }: LoadingResult<ReadonlyArray<Custodian>>,
  salesCounterPart: ISalesCounterpart | null,
): QuoteFormError[] => {
  if (!isSuccess) {
    return [CUSTODIAN_FETCH_FAILED_ERROR];
  } else if (salesCounterPart !== null && (!custodians || custodians.length === 0)) {
    return [MISSING_CUSTODIANS_ERROR];
  }

  return [];
};

const validateLoadingError = (
  loadingStatus: AddonFormLoadingStatus,
  loadingError: string | undefined,
): QuoteFormError[] => {
  if (loadingStatus === 'Error') {
    const values: { [id: string]: string } = loadingError !== undefined ? { innerError: loadingError } : {};

    return [
      {
        fields: [],
        severity: 'Error',
        translationKey: 'addon.loadingFailed',
        values,
      },
    ];
  }

  return [];
};

export function getValidationErrors(state: AppState, tabId: string): QuoteFormError[] {
  const form = getForm(state, tabId);
  const info = getInfo(state, tabId);
  if (form === undefined || info === undefined) {
    const loadingStatus = getLoadingStatus(state, tabId);
    const loadingError = getLoadingError(state, tabId);
    return validateLoadingError(loadingStatus, loadingError);
  }

  const validationErrors: QuoteFormError[] = [
    ...validateSalesCounterPart(form.selectedOnBehalfClient),
    ...validateMergeDeals(form.mergeDealsRequested, form.mergeableDeals, form.selectedTradeReference, info.isOtc),
    ...validateNotional(form.notionalType, form.addonNotional),
    ...validateNotionalInUnits(form.notionalType, form.dailyNumberOfShares, form.totalNumberOfAccumulationDays),
    ...validateDealSplit(form.dealSplit, form.notionalType, form.addonNotional),
    ...validateCustodians(form.custodians, form.selectedOnBehalfClient),
    ...validateXMargin(form.solveFor, form.xMargin),
    ...validateUpFrontFee(form.remunerationMode, form.solveFor, form.upFrontFee),
    ...validateFixedAmount(form.remunerationMode, form.solveFor, form.fixedAmount),
    ...validateOfferPrice(form.remunerationMode, form.solveFor, form.offerPrice),
    ...(form.tradeRejectionValidationErrors ?? []),
    ...(form.tradeDoneExecMessages ?? []),
  ];

  return validationErrors;
}

export function getQuoteRejections(state: AppState, tabId: string) {
  const quoteId = getLastQuoteId(state, tabId);
  return getQuoteRejectionsByQuoteId(state, quoteId);
}
